/* ============================================================
   TDOG motion engine — ported from the approved prototype.
   Reveals (tdg + legacy vocab) · odometer counters · parallax ·
   magnetic CTAs · pinned ritual scrollytelling · before/after
   slider. Zero deps, transform/opacity only, reduced-motion
   aware, Shopify theme-editor aware.
   ============================================================ */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobile = window.matchMedia('(max-width: 899px)').matches;

  /* ---------- Reveals (both vocabularies) ---------- */
  var REVEAL_SEL = '[data-tdg-reveal], .tdg-line, [data-tdg-curtain], [data-tdog-reveal]';
  function markIn(el) {
    el.classList.add('tdg-in');
    el.classList.add('tdog-in');
  }
  function initReveals(root) {
    var scope = root || document;
    var els = Array.prototype.slice.call(scope.querySelectorAll(REVEAL_SEL)).filter(function (el) {
      return !el.classList.contains('tdg-in') && !el.classList.contains('tdog-in');
    });
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(markIn);
      return;
    }

    var immediate = [], observed = [];
    els.forEach(function (el) {
      if (el.closest('[data-tdg-immediate], [data-tdog-reveal-immediate]')) immediate.push(el);
      else observed.push(el);
    });

    if (immediate.length) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { immediate.forEach(markIn); });
      });
    }
    if (observed.length) {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { markIn(e.target); obs.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
      observed.forEach(function (el) { io.observe(el); });
    }

    /* Safety net: never leave content hidden (starved rAF, odd webviews) */
    setTimeout(function () { els.forEach(markIn); }, 3000);
  }

  /* ---------- Odometer counters ---------- */
  function runCounter(el) {
    var target = parseFloat(el.getAttribute('data-tdg-count'));
    if (isNaN(target)) return;
    var dur = 1400, t0 = null;
    var isFloat = String(target).indexOf('.') > -1;
    function frame(t) {
      if (!t0) t0 = t;
      var p = Math.min((t - t0) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 4);
      var val = target * eased;
      el.textContent = isFloat ? val.toFixed(1) : Math.round(val).toString();
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }
  function initCounters(root) {
    var els = Array.prototype.slice.call((root || document).querySelectorAll('[data-tdg-count]'));
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-tdg-count'); });
      return;
    }
    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { runCounter(e.target); cio.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { cio.observe(el); });
    setTimeout(function () {
      els.forEach(function (el) {
        if (el.textContent === '0') el.textContent = el.getAttribute('data-tdg-count');
      });
    }, 4000);
  }

  /* ---------- Parallax (both vocabularies) ---------- */
  function initParallax() {
    if (reduce || mobile) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-tdg-parallax], [data-tdog-parallax]'));
    if (!els.length) return;
    var tick = false;
    function frame() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -240 || r.top > vh + 240) return;
        var speed = parseFloat(el.getAttribute('data-tdg-parallax') || el.getAttribute('data-tdog-parallax-speed') || '0.06');
        var off = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = 'translate3d(0,' + off.toFixed(1) + 'px,0)';
      });
      tick = false;
    }
    window.addEventListener('scroll', function () {
      if (!tick) { tick = true; requestAnimationFrame(frame); }
    }, { passive: true });
    window.addEventListener('resize', function () {
      if (!tick) { tick = true; requestAnimationFrame(frame); }
    }, { passive: true });
    frame();
  }

  /* ---------- Magnetic CTAs (max 2 per page) ---------- */
  function initMagnetic() {
    if (reduce || mobile || !matchMedia('(pointer:fine)').matches) return;
    Array.prototype.slice.call(document.querySelectorAll('[data-tdg-magnetic]')).slice(0, 2).forEach(function (btn) {
      var rect;
      btn.addEventListener('pointerenter', function () { rect = btn.getBoundingClientRect(); });
      btn.addEventListener('pointermove', function (e) {
        if (!rect) rect = btn.getBoundingClientRect();
        var dx = (e.clientX - rect.left - rect.width / 2) * 0.22;
        var dy = (e.clientY - rect.top - rect.height / 2) * 0.32;
        btn.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px)';
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.transition = 'transform 0.6s cubic-bezier(0.23,1,0.32,1)';
        btn.style.transform = '';
        setTimeout(function () { btn.style.transition = ''; }, 600);
      });
    });
  }

  /* ---------- Pinned ritual scrollytelling ---------- */
  function initRitual() {
    var ritual = document.querySelector('.tdg-ritual');
    if (!ritual || reduce) return;
    var cards = Array.prototype.slice.call(ritual.querySelectorAll('.tdg-ritual__card'));
    var steps = Array.prototype.slice.call(ritual.querySelectorAll('.tdg-ritual__step'));
    var ticks = Array.prototype.slice.call(ritual.querySelectorAll('.tdg-ritual__tick'));
    var countEl = ritual.querySelector('.tdg-ritual__count b');
    var n = steps.length;
    if (!n) return;
    var current = -1, tick = false;

    function frame() {
      var r = ritual.getBoundingClientRect();
      var total = r.height - window.innerHeight;
      if (total <= 0) { tick = false; return; }
      var progress = Math.min(Math.max(-r.top / total, 0), 0.9999);
      var idx = Math.min(Math.floor(progress * n), n - 1);
      var stepP = (progress * n) % 1;
      if (idx !== current) {
        cards.forEach(function (c, i) {
          c.classList.toggle('tdg-active', i === idx);
          c.classList.toggle('tdg-leaving', i < idx);
        });
        steps.forEach(function (s, i) { s.classList.toggle('tdg-active', i === idx); });
        if (countEl) countEl.textContent = '0' + (idx + 1);
        current = idx;
      }
      ticks.forEach(function (t, i) {
        t.style.setProperty('--p', i < idx ? 1 : i === idx ? stepP.toFixed(3) : 0);
      });
      tick = false;
    }
    window.addEventListener('scroll', function () {
      if (!tick) { tick = true; requestAnimationFrame(frame); }
    }, { passive: true });
    frame();
  }

  /* ---------- Before/After slider ---------- */
  function initBA(root) {
    Array.prototype.slice.call((root || document).querySelectorAll('.tdg-ba')).forEach(function (ba) {
      if (ba.dataset.tdgBound) return;
      ba.dataset.tdgBound = '1';
      function setSplit(clientX) {
        var r = ba.getBoundingClientRect();
        var p = Math.min(Math.max((clientX - r.left) / r.width, 0.06), 0.94);
        ba.style.setProperty('--split', (p * 100).toFixed(2) + '%');
      }
      var dragging = false;
      ba.addEventListener('pointerdown', function (e) { dragging = true; ba.setPointerCapture(e.pointerId); setSplit(e.clientX); });
      ba.addEventListener('pointermove', function (e) { if (dragging) setSplit(e.clientX); });
      ba.addEventListener('pointerup', function () { dragging = false; });
      ba.addEventListener('pointercancel', function () { dragging = false; });
    });
  }

  function boot(root) {
    initReveals(root);
    initCounters(root);
    initBA(root);
    if (!root) {
      initParallax();
      initMagnetic();
      initRitual();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: re-init when sections re-render */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
    initRitual();
  });
})();
